import { drugAction } from '@api'
import { useAllergy } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { allergySchema } from '@schema/allergy.schema'
import { useAppSelector } from '@store/hooks'
import { FormButton, Modal, TextAreaField } from '@ui'
import { Select } from '@ui/SearchInput/Select'
import { createOption, showToast } from '@util'
import { useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { InferType } from 'yup'

type Props = {
  onClose: () => void
}

const AddAllergies = ({ onClose }: Props) => {
  const { patientId } = useParams<string>()
  const { allergy_allergens, allergy_categories, allergy_reaction_types, allergy_reactions, allergy_severity } =
    useAppSelector(state => state.config)
  const { createAllergy } = useAllergy()
  const methods = useForm({ resolver: yupResolver(allergySchema) })
  const { handleSubmit, formState, control, watch, setValue } = methods

  const categoryVal = watch('category')

  const allergenOptions = useMemo(() => {
    if (categoryVal) {
      if (categoryVal === 'Drug') {
        return []
      }
      setValue('allergen', '')
      return allergy_allergens[categoryVal] ? allergy_allergens[categoryVal]?.map(label => createOption(label)) : []
    }
    return []
  }, [categoryVal, allergy_allergens, setValue])

  const onSubmit = async (data: InferType<typeof allergySchema>) => {
    const payload = {
      data: {
        attributes: {
          ...data
        },
        relationships: {
          patient: {
            data: {
              id: patientId,
              type: 'patient'
            }
          }
        },
        type: 'allergy'
      }
    }
    try {
      await createAllergy(payload)
      showToast('Allergy added successfully')
      onClose()
    } catch (error: any) {
      showToast(error?.response?.data?.errors![0]?.detail || 'an error occurred')
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Add Allergy">
        <form className="flex flex-col flex-1 overflow-auto" onSubmit={handleSubmit(onSubmit)}>
          <div className="w-full max-w-[550px] flex flex-col overflow-auto gap-6 p-6">
            <div className="">
              <Controller
                control={control}
                name="category"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    defaultOptions
                    error={error?.message}
                    getOptionValue={({ value }) => value}
                    isCreatable
                    label="Allergen Category"
                    onChange={(val: any) => onChange(val?.value)}
                    options={allergy_categories?.map(list => createOption(list)) || []}
                    value={{ label: value, value }}
                  />
                )}
              />
            </div>
            <div className="">
              <Controller
                control={control}
                name="allergen"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    defaultOptions
                    error={error?.message}
                    getOptionValue={({ value }) => value}
                    isAsync={categoryVal === 'Drug'}
                    isCreatable
                    label="Allergen"
                    loadOptions={drugAction}
                    onChange={(val: any) => onChange(val?.value)}
                    options={allergenOptions || []}
                    value={{ label: value, value }}
                  />
                )}
              />
            </div>
            <div className="">
              <Controller
                control={control}
                name="reaction"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    defaultOptions
                    error={error?.message}
                    getOptionValue={({ value }) => value}
                    isCreatable
                    label="Reaction"
                    onChange={(val: any) => onChange(val?.value)}
                    options={allergy_reactions?.map(list => createOption(list)) || []}
                    value={{ label: value, value }}
                  />
                )}
              />
            </div>
            <div className="">
              <Controller
                control={control}
                name="severity"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    defaultOptions
                    error={error?.message}
                    getOptionValue={({ value }) => value}
                    isCreatable
                    label="Severity"
                    onChange={(val: any) => onChange(val?.value)}
                    options={allergy_severity?.map(list => createOption(list)) || []}
                    value={{ label: value, value }}
                  />
                )}
              />
            </div>
            <div className="">
              <Controller
                control={control}
                name="reaction_type"
                render={({ field: { onChange, value } }) => (
                  <Select
                    defaultOptions
                    getOptionValue={({ value }) => value}
                    isCreatable
                    label="Reaction Type"
                    onChange={(val: any) => onChange(val?.value)}
                    options={allergy_reaction_types?.map(list => createOption(list)) || []}
                    value={{ label: value, value }}
                  />
                )}
              />
            </div>
            <div className="">
              <TextAreaField label="Note" name="note" />
            </div>
          </div>
          <FormButton isLoading={formState.isSubmitting} onClose={onClose} />
        </form>
      </Modal>
    </FormProvider>
  )
}

export default AddAllergies
